import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, subMonths, isSameMonth, isToday, differenceInCalendarDays
} from 'date-fns';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatCategory = (gl) =>
  gl ? gl.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : '';

// Base label shown on a badge (before any "(cont'd)" suffix).
const baseLabel = (position) => {
  if (position.accepted_by_name) return position.accepted_by_name;
  return formatCategory(position.grade_level) || 'Open';
};

export function PositionsCalendar({
  positions,
  month,
  onMonthChange,
  onBadgeClick,
  hasActiveFilters,
  onResetFilters,
}) {
  // Build a map of dateStr -> [{ position, label }]
  const badgesByDate = useMemo(() => {
    const map = {};
    positions.forEach((position) => {
      const rawDates = position.dates && position.dates.length
        ? position.dates
        : [position.start_date || position.date].filter(Boolean);
      const sorted = [...new Set(rawDates)].sort();
      sorted.forEach((dateStr, i) => {
        let isContd = false;
        if (i > 0) {
          try {
            isContd = differenceInCalendarDays(parseISO(dateStr), parseISO(sorted[i - 1])) !== 1;
          } catch (_e) {
            isContd = true;
          }
        }
        const label = isContd ? `${baseLabel(position)} (cont'd)` : baseLabel(position);
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push({ position, label });
      });
    });
    return map;
  }, [positions]);

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  // Any positions visible in the current month?
  const hasPositionsThisMonth = useMemo(
    () => days.some((d) => isSameMonth(d, month) && badgesByDate[format(d, 'yyyy-MM-dd')]),
    [days, month, badgesByDate]
  );

  const statusColor = (status) => {
    if (status === 'filled') return 'bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200';
    if (status === 'cancelled') return 'bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-200 line-through';
    return 'bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200';
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden relative" data-testid="positions-calendar">
      {/* Header navigation */}
      <div className="flex items-center justify-between p-3 border-b border-slate-200">
        <h3 className="text-base font-semibold text-slate-900" data-testid="calendar-month-label">
          {format(month, 'MMMM yyyy')}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="outline" size="sm" className="h-8"
            onClick={() => onMonthChange(new Date())}
            data-testid="calendar-today-btn"
          >
            Today
          </Button>
          <Button
            variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onMonthChange(subMonths(month, 1))}
            data-testid="calendar-prev-btn"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline" size="icon" className="h-8 w-8"
            onClick={() => onMonthChange(addMonths(month, 1))}
            data-testid="calendar-next-btn"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="px-2 py-1.5 text-xs font-medium text-slate-500 text-center">
            {wd}
          </div>
        ))}
      </div>

      {/* Day grid — cells stretch to the tallest cell in their row (dynamic density) */}
      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const inMonth = isSameMonth(day, month);
            const entries = badgesByDate[dateStr] || [];
            return (
              <div
                key={dateStr}
                className={`min-h-[96px] border-b border-r border-slate-100 p-1.5 ${inMonth ? 'bg-white' : 'bg-slate-50/60'}`}
                data-testid={`calendar-cell-${dateStr}`}
              >
                <div className={`text-xs mb-1 ${inMonth ? 'text-slate-600' : 'text-slate-400'} ${isToday(day) ? 'font-bold' : ''}`}>
                  <span className={isToday(day) ? 'inline-flex items-center justify-center h-5 w-5 rounded-full bg-slate-900 text-white' : ''}>
                    {format(day, 'd')}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  {entries.map(({ position, label }, idx) => (
                    <Tooltip key={`${position.position_id}-${idx}`}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onBadgeClick(position)}
                          className={`w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] leading-tight font-medium cursor-pointer ${statusColor(position.status)}`}
                          data-testid={`calendar-badge-${position.position_id}-${dateStr}`}
                        >
                          {label}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-0.5 text-xs">
                          {position.accepted_by_name && (
                            <div><span className="text-slate-400">Sub:</span> {position.accepted_by_name}</div>
                          )}
                          <div><span className="text-slate-400">Category:</span> {formatCategory(position.grade_level) || 'Open'}</div>
                          <div><span className="text-slate-400">Times:</span> {position.start_time} - {position.end_time}</div>
                          <div><span className="text-slate-400">Status:</span> {position.accepted_by_name ? 'Filled' : (position.status === 'cancelled' ? 'Cancelled' : 'Open')}</div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Empty-state overlay for the current month */}
      {!hasPositionsThisMonth && (
        <div className="absolute inset-0 top-[90px] flex items-center justify-center pointer-events-none">
          <div className="bg-white/90 border border-slate-200 rounded-lg px-4 py-3 text-center pointer-events-auto shadow-sm" data-testid="calendar-empty-overlay">
            <p className="text-sm text-slate-600 mb-2">No positions match active filters for this month</p>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={onResetFilters} data-testid="calendar-reset-filters-btn">
                Reset Filters
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
